<template>
  <div class="live-h5-container">
    <iframe
      v-if="live_url"
      :src="live_url"
      class="live-h5-iframe"
      frameborder="0"
      allow="microphone; camera; fullscreen; autoplay; encrypted-media; picture-in-picture"
      allowfullscreen
    ></iframe>
    <div v-else class="live-h5-loading">加载中...</div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import utils from '@/common/utils'
import config from '@/common/config'
import liveModel from './model/model.js'

const route = useRoute()
const router = useRouter()

const livingid = ref('')
const key = ref('')
const live_url = ref('')
const unionid = ref('')
let heartbeat_timer = null
const HEARTBEAT_INTERVAL = 30

/**
 * 生成微信网站应用授权URL（PC扫码登录）
 * state 固定为 "vhlive" 字符串，不携带业务参数
 * livingid/key 在跳转前已存入 sessionStorage，回调回来时从缓存恢复
 * @returns {string} 微信授权URL
 */
const getWechatAuthUrl = () => {
  const appId = config.webAppId
  const redirectUri = encodeURIComponent(config.webRedirectUrl)
  return 'https://open.weixin.qq.com/connect/qrconnect?appid=' + appId +
    '&redirect_uri=' + redirectUri +
    '&response_type=code' +
    '&scope=snsapi_login' +
    '&state=vhlive' +
    '#wechat_redirect'
}

/**
 * 判断是否在微信内浏览器中
 * @returns {boolean}
 */
const isWechatBrowser = () => {
  const ua = navigator.userAgent.toLowerCase()
  return ua.indexOf('micromessenger') > -1
}

/**
 * 生成微信公众号授权URL（微信内浏览器H5网页授权）
 * 与网站应用扫码授权的区别：
 * - 授权接口为 oauth2/authorize（非 qrconnect）
 * - scope 为 snsapi_userinfo（非 snsapi_login）
 * - 使用公众号AppID（非网站应用AppID）
 * @returns {string} 微信公众号授权URL
 */
const getWxmpAuthUrl = () => {
  const appId = config.wxmpAppId
  const redirectUri = encodeURIComponent(config.webRedirectUrl)
  return 'https://open.weixin.qq.com/connect/oauth2/authorize?appid=' + appId +
    '&redirect_uri=' + redirectUri +
    '&response_type=code' +
    '&scope=snsapi_userinfo' +
    '&state=vhlive' +
    '#wechat_redirect'
}

/**
 * 获取本地存储的登录态
 * @param {string} livingid 直播ID
 * @returns {Object|null} 登录态数据
 */
const getStoredLogin = (livingid) => {
  try {
    const data = localStorage.getItem('live_login_' + livingid)
    return data ? JSON.parse(data) : null
  } catch (e) {
    return null
  }
}

/**
 * 保存登录态到本地存储
 * @param {string} livingid 直播ID
 * @param {Object} data 后端返回的登录数据
 */
const saveLogin = (livingid, data) => {
  localStorage.setItem('live_login_' + livingid, JSON.stringify({
    unionid: data.unionid,
    live_url: data.url,
    nickname: data.nickname,
    avatar: data.avatar,
    key: data.key,
    login_time: Date.now()
  }))
}

/**
 * 发送心跳请求，保持在线状态（后端60秒无心跳视为离线）
 */
const sendHeartbeat = () => {
  if (!unionid.value || !livingid.value) return
  utils.ajax('POST', config.vhApi + '/miniapp/live_heartbeat', {
    unionid: unionid.value,
    livingid: livingid.value
  }).then(() => {}, () => {})
}

/**
 * 启动心跳定时器，立即发一次，之后每30秒一次
 */
const startHeartbeat = () => {
  stopHeartbeat()
  sendHeartbeat()
  heartbeat_timer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL * 1000)
}

/**
 * 停止心跳定时器
 */
const stopHeartbeat = () => {
  if (heartbeat_timer) {
    clearInterval(heartbeat_timer)
    heartbeat_timer = null
  }
}

/**
 * 发送离开直播间请求
 */
const sendLeave = () => {
  if (!unionid.value || !livingid.value) return
  utils.ajax('POST', config.vhApi + '/miniapp/live_leave', {
    unionid: unionid.value,
    livingid: livingid.value
  }).then(() => {}, () => {})
}

/**
 * 页面可见性变化处理
 * 参考 小程序方案B：切走停心跳，切回启心跳，不刷新 iframe（保持 audio session 连续）
 */
const handleVisibilityChange = () => {
  if (document.hidden) {
    stopHeartbeat()
  } else {
    startHeartbeat()
  }
}

onMounted(async () => {
  // livingid/key 从URL读，兜底从sessionStorage读（跳转前已缓存）
  // state 固定为 "vhlive" 字符串，不携带业务参数
  livingid.value = route.query.livingid || sessionStorage.getItem('live_pending_livingid') || ''
  key.value = route.query.key || sessionStorage.getItem('live_pending_key') || ''
  const code = route.query.code || ''

  if (!livingid.value) {
    alert('缺少直播参数')
    return
  }

  // 保存当前直播参数，授权回调后若URL未带livingid/key可从sessionStorage恢复
  sessionStorage.setItem('live_pending_livingid', livingid.value)
  sessionStorage.setItem('live_pending_key', key.value)

  if (code) {
    // 从微信回调回来，调后端登录
    // 微信浏览器走公众号登录，普通浏览器走网站应用登录
    try {
      const loginFn = isWechatBrowser() ? liveModel.wxmp_login : liveModel.web_login
      const res = await loginFn({ code: code, livingid: livingid.value, key: key.value })
      if (res.code == 0) {
        unionid.value = res.unionid
        live_url.value = res.url
        saveLogin(livingid.value, res)
        // 清理 URL 的 code，避免刷新重复使用 code（微信 code 一次性）
        router.replace({ path: '/live', query: { livingid: livingid.value, key: key.value } })
        startHeartbeat()
      } else {
        alert(res.message || '登录失败')
      }
    } catch (e) {
      alert('登录失败，请重试')
    }
  } else {
    // 检查本地登录态
    const stored = getStoredLogin(livingid.value)
    if (stored && stored.live_url) {
      unionid.value = stored.unionid
      live_url.value = stored.live_url
      startHeartbeat()
    } else {
      // 跳转微信授权：微信浏览器走公众号授权，普通浏览器走网站应用扫码
      window.location.href = isWechatBrowser() ? getWxmpAuthUrl() : getWechatAuthUrl()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  stopHeartbeat()
  sendLeave()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>
<style>
    #app{
    padding: 0 !important;
    margin: 0 !important;
  }
</style>
<style scoped>

.live-h5-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f8f8;
}

.live-h5-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.live-h5-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 16px;
}
</style>
